import { type JSX } from 'react';
import { useDictation } from './useDictation';
import { MicIcon } from './MicIcon';

// A mic toggle that dictates the user's own words into a field (verbatim). Renders
// nothing where the browser has no Web Speech API. Styling rides the shared `.btn`
// classes; the mic glyph inherits the button colour (charcoal idle, red while
// listening) via `currentColor`.

export function DictationButton({
  onText,
  lang,
  disabled,
  label = 'Dictate',
  className = 'btn btn-ghost btn-sm',
}: {
  /** Called with each finalised spoken segment; append it to your field. */
  onText: (segment: string) => void;
  lang?: string;
  disabled?: boolean;
  label?: string;
  className?: string;
}): JSX.Element | null {
  const { supported, listening, error, toggle } = useDictation(onText, lang ? { lang } : undefined);
  if (!supported) return null;
  return (
    <span
      style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}
    >
      <button
        type="button"
        className={className}
        aria-pressed={listening}
        aria-label={listening ? 'Stop dictation' : 'Dictate your answer'}
        disabled={disabled}
        onClick={toggle}
        title={listening ? 'Stop dictation' : 'Dictate your answer'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          ...(listening ? { color: 'var(--color-danger, #a4262c)' } : {}),
        }}
      >
        <MicIcon />
        {listening ? 'Listening…' : label}
      </button>
      {error && (
        <span className="error" style={{ fontSize: 12 }}>
          {error}
        </span>
      )}
    </span>
  );
}
